import {
  Combobox,
  InputChips,
  initialStatePhoneNumber,
  InputPhone,
  mobilePhoneToString,
  PhoneNumber,
  validMobileForInputPhone,
  InputTextCustom,
  InputSwitch,
} from 'medere-ui-framework'
import { useEffect, useMemo, useState } from 'react'
import { ComboDataDTO } from '../../../dto/ComboDataDTO'
import { HealthEntitySaveDTO } from '../../../dto/HealthEntitySaveDTO'
import { useManagerService } from '../../../servicesHooks/Manager/useManagerService'
import { useHealthEntityService } from '../../../servicesHooks/HealthEntity/useHealthEntityService'
import HealthEntityAddressInput from '../components/HealthEntityAddressInput'
import type { HealthEntityGeneralValidationErrors } from '../HealthEntityCrudPanel'

interface HealthEntityGeneralTabProps {
  healthEntity: HealthEntitySaveDTO
  setHealthEntity: React.Dispatch<React.SetStateAction<HealthEntitySaveDTO>>
  validationErrors?: HealthEntityGeneralValidationErrors
  isMobile?: boolean
  onClearValidationError?: (field: 'commercialname') => void
  readOnly?: boolean
}

interface ReadOnlyFieldProps {
  label: string
  value: string | null | undefined
  className?: string
  emptyText?: string
}

function formatReadOnlyValue(value: string | null | undefined, emptyText = 'Sin datos'): string {
  const normalized = String(value ?? '').trim()
  return normalized !== '' ? normalized : emptyText
}

export default function HealthEntityGeneralTab({
  healthEntity,
  setHealthEntity,
  validationErrors = {},
  isMobile = false,
  onClearValidationError,
  readOnly = false,
}: Readonly<HealthEntityGeneralTabProps>) {
  const { getHealthEntityClasses } = useHealthEntityService()
  const { getAllAddressTypesForPatientEntity } = useManagerService()
  const [healthEntityPhone, setHealthEntityPhone] = useState<PhoneNumber>(
    healthEntity.phone ? validMobileForInputPhone(healthEntity.phone) : initialStatePhoneNumber,
  )

  useEffect(() => {
    const nextPhoneRaw = (healthEntity.phone ?? '').trim()

    setHealthEntityPhone((currentPhone) => {
      const currentPhoneRaw = mobilePhoneToString(currentPhone).trim()
      if (nextPhoneRaw === currentPhoneRaw) {
        return currentPhone
      }
      return nextPhoneRaw !== '' ? validMobileForInputPhone(nextPhoneRaw) : initialStatePhoneNumber
    })
  }, [healthEntity.phone])

  const selectedClassification = useMemo<ComboDataDTO | null>(() => {
    const classificationId = Number(healthEntity.classification ?? -1)
    const classificationName = String(healthEntity.classificationdesc ?? '').trim()

    if (classificationId > 0) {
      return { id: classificationId, name: classificationName }
    }
    if (classificationName !== '') {
      return { id: -1, name: classificationName }
    }
    return null
  }, [healthEntity.classification, healthEntity.classificationdesc])

  function ReadOnlyField({ label, value, className = '', emptyText = 'Sin datos' }: Readonly<ReadOnlyFieldProps>) {
    const normalizedValue = formatReadOnlyValue(value, emptyText)
    const isEmptyValue = normalizedValue === emptyText

    return (
      <div className={`health-entity-general-field health-entity-readonly-field ${className}`.trim()}>
        <span className='health-entity-readonly-field-label'>{label}</span>
        {isEmptyValue ? (
          <em
            className='health-entity-readonly-field-value health-entity-readonly-field-value-muted'
            style={{ fontFamily: 'sans-serif', fontStyle: 'italic' }}
          >
            {normalizedValue}
          </em>
        ) : (
          <span className='health-entity-readonly-field-value'>{normalizedValue}</span>
        )}
      </div>
    )
  }

  const phoneValue = formatReadOnlyValue(healthEntity.phone)
  const emailValue = formatReadOnlyValue(
    String(healthEntity.email ?? '')
      .split(';')
      .map((item) => item.trim())
      .filter(Boolean)
      .join(' ; '),
  )

  return (
    <div className='health-entity-general-tab'>
      <div className='health-entity-general-grid'>
        {readOnly ? (
          <>
            <ReadOnlyField
              className='health-entity-field-commercial'
              label='Nombre Comercial'
              value={healthEntity.commercialname}
            />

            <ReadOnlyField
              className='health-entity-field-classification'
              label='Clasificación'
              value={healthEntity.classificationdesc}
            />

            <ReadOnlyField
              className='health-entity-field-id'
              label='ID'
              value={healthEntity.externalid}
            />

            <ReadOnlyField
              className='health-entity-field-active'
              label='Estado'
              value={healthEntity.active ? 'Activo' : 'Inactivo'}
            />

            <ReadOnlyField
              className='health-entity-field-phone'
              label='Teléfono / Celular'
              value={phoneValue}
            />

            <ReadOnlyField
              className='health-entity-field-email'
              label='E-mail (puede contener más de un correo)'
              value={emailValue}
            />
          </>
        ) : (
          <>
            <div className='health-entity-general-field health-entity-field-commercial'>
              <label className='block mb-2'>Nombre Comercial <span className='text-red-500'>*</span></label>
              <InputTextCustom
                className='w-full'
                error={!!validationErrors.commercialname}
                caption={validationErrors.commercialname}
                value={healthEntity.commercialname}
                disable={readOnly}
                onChange={(e) => {
                  const value = e.target.value
                  onClearValidationError?.('commercialname')
                  setHealthEntity(prev => {
                    const shouldMirrorBusinessName = prev.businessname.trim() === '' || prev.businessname === prev.commercialname
                    return {
                      ...prev,
                      commercialname: value,
                      businessname: shouldMirrorBusinessName ? value : prev.businessname,
                    }
                  })
                }}
              />
            </div>

            <div className='health-entity-general-field health-entity-field-classification'>
              <Combobox
                className='w-full'
                label='Clasificación'
                optionLabel='name'
                value={selectedClassification}
                getItems={getHealthEntityClasses}
                placeholder={readOnly ? '' : 'Seleccionar'}
                disable={readOnly}
                setValue={(selected: ComboDataDTO | null) => {
                  const nextClassification = selected?.id && selected.id > 0 ? selected.id : null
                  const nextClassificationDesc = selected?.name ?? ''
                  setHealthEntity(prev => {
                    if (
                      prev.classification === nextClassification
                      && prev.classificationdesc === nextClassificationDesc
                    ) {
                      return prev
                    }
                    return {
                      ...prev,
                      classification: nextClassification,
                      classificationdesc: nextClassificationDesc,
                    }
                  })
                }}
              />
            </div>

            <div className='health-entity-general-field health-entity-field-id'>
              <label className='block mb-2'>ID</label>
              <InputTextCustom
                className='w-full'
                value={healthEntity.externalid}
                disable={readOnly}
                onChange={(e) => setHealthEntity(prev => ({ ...prev, externalid: e.target.value }))}
              />
            </div>

            <div className='health-entity-general-field health-entity-field-active'>
              <InputSwitch
                className='health-entity-active-switch'
                checked={healthEntity.active}
                disabled={readOnly}
                onChange={(e) => {
                  setHealthEntity(prev => ({
                    ...prev,
                    active: !!e.value,
                  }))
                }}
              />
              <span className='health-entity-active-status'>{healthEntity.active ? 'Activo' : 'Inactivo'}</span>
            </div>

            <div className='health-entity-general-field health-entity-field-phone'>
              <label className='block mb-2'>Teléfono / Celular</label>
              <InputPhone
                className='w-full'
                value={healthEntityPhone}
                disable={readOnly}
                setValue={(phoneNumber: PhoneNumber) => {
                  const nextPhone = mobilePhoneToString(phoneNumber).trim()
                  setHealthEntityPhone(phoneNumber)
                  setHealthEntity(prev => {
                    if ((prev.phone ?? '').trim() === nextPhone) {
                      return prev
                    }
                    return { ...prev, phone: nextPhone }
                  })
                }}
              />
            </div>

            <div className='health-entity-general-field health-entity-field-email'>
              <label className='block mb-2'>E-mail (puede contener más de un correo)</label>
              <InputChips
                containerClassName='w-full'
                wrapperClassName='w-full'
                separator=';'
                keyFilter='email'
                value={healthEntity.email && healthEntity.email !== '' ? healthEntity.email.split(';') : []}
                onChange={(values) => setHealthEntity(prev => ({ ...prev, email: values.join(';') }))}
                placeholder='Ingresar correos'
              />
            </div>
          </>
        )}
      </div>

      <div className='health-entity-address-section'>
        <HealthEntityAddressInput
          getAllAddressTypes={getAllAddressTypesForPatientEntity}
          addresses={healthEntity.addresses}
          setAddresses={(addresses) => setHealthEntity(prev => ({ ...prev, addresses }))}
          isMobile={isMobile}
          readOnly={readOnly}
          modalDeletedConfirmed
        />
      </div>
    </div>
  )
}


